/**
 * نمای نتیجه — پاسخ مستقیم، استدلال، شرط‌ها، ریسک‌ها و ارجاع‌ها.
 *
 * ترتیب بخش‌ها ثابت است: از قاطع‌ترین به تفصیلی‌ترین. کاربر هیئت‌مدیره اول
 * می‌خواهد بداند «چه بود»، بعد «چرا».
 */

import {
  AlertTriangle,
  ArrowRight,
  BadgeCheck,
  Brain,
  CircleHelp,
  Crosshair,
  Gauge,
  Lightbulb,
  ListChecks,
  Milestone,
  Quote,
  RefreshCw,
  Scale,
  ShieldAlert,
  ThumbsDown,
  ThumbsUp,
} from 'lucide-react'

import { Bullets, Card, SectionHeader } from '@/components/ui/primitives'
import { useMeta } from '@/lib/meta-context'

import { resolveCitations } from './citations'

import type { DecisionAdvice, SupplementaryEvidence } from '@/lib/types'

interface ResultViewProps {
  advice: DecisionAdvice
  supplementary: SupplementaryEvidence[]
}

export const ResultView = ({ advice, supplementary }: ResultViewProps) => {
  const { meta } = useMeta()

  return (
    <div className="space-y-4">
      {/*
        پاسخ مستقیم، پیش از هر تفصیلی — و با بزرگ‌ترین مقیاس متنِ این نما.
        نسخه‌ی قبلی همان اندازه‌ی بقیه‌ی متن‌ها بود و در میان کارت‌ها گم می‌شد.
      */}
      {/*
        بازقالب‌بندی پیش از پاسخ مستقیم می‌آید. اگر هدف با مکانیزم مخلوط شده
        بوده، پاسخ به هدف داده شده و خواننده باید همان اول بداند به چه چیزی
        جواب داده شده است.
      */}
      {advice.problem_reframing !== '' && (
        <Card>
          <SectionHeader
            title="صورت مسئله"
            caption="هدف سطح بالاتر، جدا از مکانیزمی که پیشنهاد شده بود."
            icon={<Crosshair className="size-4" />}
          />
          <p className="p-5 leading-relaxed text-ink">{advice.problem_reframing}</p>
        </Card>
      )}

      <Card tone="feature">
        <SectionHeader title="پاسخ مستقیم" icon={<BadgeCheck className="size-4" />} />
        <p className="relative p-5 text-lg leading-relaxed font-medium text-ink">
          {advice.direct_answer}
        </p>
      </Card>

      {/*
        ⚠️ این دو کارت بلافاصله پس از پاسخ مستقیم می‌آیند و پیش از استدلال.
        بازخورد کاربران این بود که خروجی «فقط می‌گوید موافقم یا مخالفم»؛ پاسخ
        آن، راهِ رسیدن به موافقت و مرزِ رسیدن به مخالفت است، نه استدلال بیشتر.
      */}
      {(advice.agreement_conditions.length > 0 ||
        advice.opposition_triggers.length > 0) && (
        <div className="grid gap-4 lg:grid-cols-2">
          {advice.agreement_conditions.length > 0 && (
            <Card className="p-5">
              <Bullets
                title="چه کار کنید موافقم"
                items={advice.agreement_conditions}
                icon={<ThumbsUp className="size-3.5" aria-hidden />}
              />
            </Card>
          )}
          {advice.opposition_triggers.length > 0 && (
            <Card className="p-5">
              <Bullets
                title="چه کار کنید مخالفم"
                items={advice.opposition_triggers}
                icon={<ThumbsDown className="size-3.5" aria-hidden />}
              />
            </Card>
          )}
        </div>
      )}

      {/* خط قرمز جدا از شرط نمایش داده می‌شود، چون تلاش برای «برآورده کردنِ»
          آن بی‌فایده است و کاربر باید همان اول بداند. */}
      {advice.fundamental_objection !== '' && (
        <Card>
          <SectionHeader
            title="مانع بنیادی"
            caption="این با اصلاح ابعاد تصمیم برطرف نمی‌شود."
            icon={<ShieldAlert className="size-4" />}
          />
          <p className="p-5 leading-relaxed text-ink">{advice.fundamental_objection}</p>
        </Card>
      )}

      <Card>
        <SectionHeader
          title={principalHeading(meta.profile.principal_name)}
          icon={<Brain className="size-4" />}
        />
        <p className="p-5 leading-relaxed text-ink">{advice.likely_principal_view}</p>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="p-5">
          <Bullets
            title="استدلال"
            items={advice.reasoning}
            icon={<ListChecks className="size-3.5" aria-hidden />}
          />
        </Card>
        <Card className="p-5">
          <Bullets
            title="شرط‌های کلیدی"
            items={advice.key_conditions}
            icon={<ListChecks className="size-3.5" aria-hidden />}
          />
        </Card>
        {/* مسیر مرحله‌ای فقط وقتی کارت می‌گیرد که وجود داشته باشد: کارت خالی
            به کاربر می‌گوید چیزی هست که نیست. */}
        {advice.staged_path.length > 0 && (
          <Card className="p-5">
            <Bullets
              title="مسیر مرحله‌ای"
              items={advice.staged_path}
              icon={<Milestone className="size-3.5" aria-hidden />}
            />
          </Card>
        )}
        <Card className="p-5">
          <Bullets
            title="ریسک‌ها"
            items={advice.risks}
            icon={<AlertTriangle className="size-3.5" aria-hidden />}
          />
        </Card>
        <Card className="p-5">
          <Bullets
            title="فرض‌ها"
            items={advice.assumptions}
            icon={<Lightbulb className="size-3.5" aria-hidden />}
          />
        </Card>
        <Card className="p-5">
          <Bullets
            title="پرسش‌هایی که محتمل است بپرسد"
            items={advice.questions_principal_would_ask}
            icon={<CircleHelp className="size-3.5" aria-hidden />}
          />
        </Card>
        <Card className="p-5">
          <Bullets
            title="چه چیزی می‌تواند پاسخ را عوض کند"
            items={advice.what_could_change_the_answer}
            icon={<RefreshCw className="size-3.5" aria-hidden />}
          />
        </Card>
      </div>

      {/* شاهد مخالف کنار اطمینان است چون هر دو یک پرسش را جواب می‌دهند: این
          برآورد کجا می‌لنگد. */}
      {advice.counter_evidence !== '' && (
        <Card>
          <SectionHeader
            title="شاهد مخالف موضع"
            caption="شاهدی که این موضع را ضعیف می‌کند، و دلیل برنگشتن حکم."
            icon={<Scale className="size-4" />}
          />
          <p className="p-5 leading-relaxed text-ink">{advice.counter_evidence}</p>
        </Card>
      )}

      <Card>
        <SectionHeader title="توضیح اطمینان" icon={<Gauge className="size-4" />} />
        <p className="p-5 text-sm leading-relaxed text-ink-muted">
          {advice.confidence_explanation}
        </p>
        {advice.scale_assessment !== '' && (
          <div className="border-t border-line p-5">
            <p className="mb-1.5 text-xs font-semibold text-ink">مرتبه‌ی مبلغ</p>
            <p className="text-sm leading-relaxed text-ink-muted">
              {advice.scale_assessment}
            </p>
          </div>
        )}
      </Card>

      {advice.decisive_question !== '' && (
        <Card>
          <SectionHeader
            title="پرسش تعیین‌کننده"
            caption="پاسخ این یک پرسش، جهت تصمیم را عوض می‌کند."
            icon={<CircleHelp className="size-4" />}
          />
          <p className="p-5 leading-relaxed font-medium text-ink">
            {advice.decisive_question}
          </p>
        </Card>
      )}

      {/* اقدام بعدی: تنها بلوکِ رنگی این نما، چون تنها چیزی است که کاربر باید
          پس از خواندن انجام دهد. */}
      <Card className="border-accent-line bg-linear-100 from-accent-soft to-surface-raised">
        <SectionHeader
          title="اقدام بعدی پیشنهادی"
          icon={<ArrowRight className="size-4" />}
          className="border-accent-line"
        />
        <p className="p-5 leading-relaxed font-medium text-ink">{advice.recommended_next_action}</p>
      </Card>

      {advice.source_citations.length > 0 && (
        <Card>
          <SectionHeader
            title="ارجاع‌ها"
            caption="هر ارجاع به یک اصل تصمیم یا بخش سند پشتیبان اشاره دارد و قابل بررسی است."
            icon={<Quote className="size-4" />}
          />
          <ul className="divide-y divide-line">
            {resolveCitations(advice.source_citations, supplementary).map((citation) => (
              <li
                key={`${citation.itemId}-${citation.usedFor}`}
                className="p-4 text-sm transition-colors hover:bg-surface-sunken/60"
              >
                <p className="font-medium text-ink">{citation.usedFor}</p>
                <p className="mt-1 text-xs text-ink-muted">{citation.text}</p>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  )
}

/**
 * عنوان بخش برآورد.
 *
 * ⚠️ لحن سوم‌شخص است، نه اول‌شخص. جعل هویت ممنوع است: «برآورد محتمل این است
 * که …» درست است و «من با این تصمیم مخالفم» نامعتبر.
 */
const principalHeading = (principalName: string): string =>
  `برآورد منطق تصمیم ${principalName}`


